

























import { abilities } from "@/plugins/casl";
import { subject } from "@casl/ability";
import { computed, defineComponent, onMounted, ref } from "@vue/composition-api";
import Page from "@/components/layout/page.vue";
import PageHeader from "@/components/navigation/page-header.vue";
import { useRouter } from "@/router/router";
import { useCommittable } from "@/composables/committable";
import { User } from "@/swagger/models";
import { userService } from "@/services/user";
import DesksTable from "@/components/tables/desks-table.vue";
import ReservationsTable from "@/components/tables/reservations/reservations-table.vue";
import UserTeams from "@/components/teams/user-teams.vue";
import { UserRole, UserRoleDisplay } from "@/types/roles";
import CommitResetBar from "@/components/data/commit-reset-bar.vue";
import TabsUser from "@/components/tabs/user.vue";

interface OktaUser {
    id: string;
    activated: string;
    created: string;
    lastLogin: string;
    lastUpdated: string;
    passwordChanged: string;
    credentials: {
        recovery_question: {
            question: string;
        };
    };
    profile: {
        email: string;
        firstName: string;
        lastName: string;
        login: string; // also email
        mobilePhone?: string;
        secondEmail?: string;
        userid: string; // assigned by us
        userType?: string; // assigned by us
    };
    status: string;
    statusChanged: string;
}
export default defineComponent({
    name: "user",
    props: {},
    components: { TabsUser, CommitResetBar, UserTeams, ReservationsTable, Page, PageHeader, DesksTable },
    setup(props, context) {
        const loading = ref(true);
        const user = ref<User>();
        const activateNewUser = ref(true);
        const userRole = ref<UserRole>(null);

        const { route, router } = useRouter(context);

        const { clone, commit, revert, isDirty } = useCommittable<User>({
            originalValue: user,
            watchOriginal: true,
            async onCommit() {
                try {
                    loading.value = true;
                    let newUser: User;

                    if (clone.value.id) {
                        // Update the user
                        // @ts-ignore (maxReservations is object in swagger model)
                        newUser = await userService.patch(user.value.id, clone.value);

                        // Did the role change? Update it
                        if (userRole.value !== (newUser.okta as OktaUser)?.profile?.userType) {
                            await userService.setRole(newUser.id, userRole.value);
                        }
                    } else {
                        // Create the user
                        newUser = await userService.create({ ...clone.value, activate: activateNewUser.value });

                        // Set the role
                        await userService.setRole(newUser.id, userRole.value);

                        // Update the url to reflect the created user
                        await router.replace({ params: { userId: newUser.id.toString() } });
                    }

                    return newUser;
                } catch (e) {
                    // The services will have handled showing a toast at this point
                    // console.warn(e);
                } finally {
                    loading.value = false;
                }
            },
        });

        const loadUser = async () => {
            if (route.value.params.userId !== "new") {
                user.value = await userService.get(parseInt(route.value.params.userId));
                userRole.value = ((user.value?.okta as OktaUser)?.profile?.userType as UserRole) || UserRole.user;
            } else {
                user.value = await userService.build({
                    customerId: parseInt(route.value.params.customerId),
                });
                userRole.value = UserRole.user;
            }

            revert(true);
            loading.value = false;
        };

        onMounted(async () => {
            await loadUser();
        });

        const roles = computed(() => {
            const roles = [
                // Hid some as they are not used at the moment
                { label: UserRoleDisplay.wbAdmin, value: UserRole.wbAdmin },
                { label: UserRoleDisplay.customerAdmin, value: UserRole.customerAdmin },
                // { label: UserRoleDisplay.customerManager, value: UserRole.customerManager },
                // { label: UserRoleDisplay.customerReceptionist, value: UserRole.customerReceptionist },
                { label: UserRoleDisplay.user, value: UserRole.user },
            ];

            // Return only the ones that the user is allowed to assign, unless the user has a role already
            return roles.filter(
                (r) =>
                    context.root.$can("update", "UserRole", r.value) ||
                    (user.value?.okta as OktaUser)?.profile.userType === r.value,
            );
        });

        const activate = async (activate: boolean) => {
            try {
                loading.value = true;
                await userService.activate(user.value.id, activate);
                await loadUser();
            } catch (e) {
            } finally {
                loading.value = false;
            }
        };

        const resetPassword = async () => {
            try {
                loading.value = true;
                await userService.resetPassword(user.value.id);
                await loadUser();
            } catch (e) {
            } finally {
                loading.value = false;
            }
        };

        const suspend = async (suspend: boolean) => {
            try {
                loading.value = true;
                await userService.suspend(user.value.id, suspend);
                await loadUser();
            } catch (e) {
            } finally {
                loading.value = false;
            }
        };

        const unlock = async () => {
            try {
                loading.value = true;
                await userService.unlock(user.value.id);
                await loadUser();
            } catch (e) {
            } finally {
                loading.value = false;
            }
        };

        const canEdit = computed(() => {
            if (loading.value) return false;
            // @ts-ignore
            if (route.value.params.userId === "new" && abilities.can("create", "User")) return true;
            // @ts-ignore
            else if (abilities.can("update", subject("User", user.value))) return true;
            return false;
        });

        const userIsStaged = computed(() => (user.value.okta as OktaUser)?.status === "STAGED");
        const userIsActive = computed(() => (user.value.okta as OktaUser)?.status === "ACTIVE");
        const userIsSuspended = computed(() => (user.value.okta as OktaUser)?.status === "SUSPENDED");
        const userIsDeprovisioned = computed(() => (user.value.okta as OktaUser)?.status === "DEPROVISIONED");
        const userIsLocked = computed(() => (user.value.okta as OktaUser)?.status === "LOCKED_OUT");

        return {
            loading,
            user,
            clone,
            commit,
            revert,
            isDirty,
            customerId: parseInt(route.value.params.customerId),
            activateNewUser,
            roles,
            userRole,
            activate,
            suspend,
            unlock,
            userIsActive,
            userIsSuspended,
            userIsLocked,
            userIsDeprovisioned,
            userIsStaged,
            resetPassword,
            canEdit,
        };
    },
});
